import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getOptimizedImageUrl, generateSrcSet, supportsWebP } from '@/utils/imageOptimization';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
  priority?: boolean; // Skip lazy loading for critical images
  sizes?: string;
  srcSet?: string;
  width?: number;
  height?: number;
  responsive?: boolean; // Enable responsive srcSet generation
}

// Generate proper placeholder
const createPlaceholderSVG = (width: number = 400, height: number = 300) => {
  const svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
    <rect width="100%" height="100%" fill="#f3f4f6"/>
    <rect x="20%" y="40%" width="60%" height="20%" fill="#e5e7eb" rx="4"/>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const LazyImage = ({ 
  src, 
  alt, 
  className, 
  placeholder,
  onLoad,
  onError,
  priority = false,
  sizes = "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw",
  srcSet,
  width,
  height,
  responsive = true
}: LazyImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<'webp' | 'original'>('original');
  const imgRef = useRef<HTMLImageElement>(null);

  // Generate proper placeholder URL
  const placeholderSrc = placeholder || createPlaceholderSVG(width, height);
  
  // Generate optimized URLs and srcSets
  const optimizedSrc = getOptimizedImageUrl(src, width, height, supportsWebP ? 'webp' : undefined);
  const fallbackSrc = getOptimizedImageUrl(src, width, height);
  
  const responsiveSrcSet = responsive && !srcSet ? 
    generateSrcSet(src, [400, 600, 800, 1200, 1600]) : srcSet;

  useEffect(() => {
    if (priority) return; // Skip observer for priority images

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    // Try fallback format if WebP fails
    if (currentFormat === 'webp') {
      setCurrentFormat('original');
      setHasError(false);
      return;
    }
    setHasError(true);
    onError?.();
  };

  // Determine which image source to use - never return undefined!
  const getImageSrc = () => {
    if (!isInView) return placeholderSrc;
    if (hasError) return placeholderSrc;
    return currentFormat === 'webp' ? optimizedSrc : fallbackSrc;
  };

  const getImageSrcSet = () => {
    if (!isInView || hasError) return undefined;
    return responsiveSrcSet;
  };

  return (
    <div className={cn("relative overflow-hidden bg-muted", className)}>
      {/* Single image element - no broken icon jank */}
      <img
        ref={imgRef}
        src={getImageSrc()}
        srcSet={getImageSrcSet()}
        sizes={isInView ? sizes : undefined}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          isLoaded && isInView && !hasError ? "opacity-100" : "opacity-70",
          className
        )}
        onLoad={handleLoad}
        onError={handleError}
      />
      
      {/* Loading indicator overlay */}
      {!isLoaded && isInView && !hasError && (
        <div className="absolute inset-0 bg-muted/50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Error state */}
      {hasError && (
        <div className={cn("absolute inset-0 bg-muted/80 flex items-center justify-center text-muted-foreground text-sm", className)}>
          Image unavailable
        </div>
      )}
    </div>
  );
};

export default LazyImage;